'use client';

import { type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ExternalLink, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Link } from '@/i18n/routing';
import { OcrResultsPage } from './ocr-results-page';
import type { ResultsDeepLink } from '../utils/results-deep-link';
import type { OcrResult } from '../types';

interface ContainerResultsOverlayProps {
  /** The container whose results to show; null keeps the overlay closed. */
  containerId: number | null;
  containerLabel: string;
  /**
   * Page or field to land on, already parsed off the overlay's own hash by the
   * listing. The panel cannot read it itself here — the fragment names the
   * overlay (`#results-view/<id>/…`), not a target.
   */
  deepLink: ResultsDeepLink | null;
  /**
   * Passed straight through to the panel's per-page slot.
   *
   * Exists so the QA rejection reasons the standalone route renders can appear
   * here too. It is a prop rather than an import because the component that
   * renders them lives in `features/ocr-qa`, which already imports FROM this
   * feature — pulling it in here would close that loop. The app layer, which
   * belongs to neither feature, supplies it.
   */
  renderPageFooter?: (
    result: OcrResult,
    pageIndexInResult: number,
    isFirstGroupForResult: boolean
  ) => ReactNode;
  onClose: () => void;
}

/**
 * The full extraction panel, shown IN the OCR screen (OMA-635).
 *
 * "View results" used to hand the operator off to
 * `/dashboard/ocr-containers/<id>` — a route change that tore down the
 * listing's query cache, table state, filters and scroll position, all of which
 * had to be rebuilt on the way back. Checking three containers meant six
 * full-page navigations. The same component renders here instead, over the
 * listing, so closing it returns to exactly the row that was clicked.
 *
 * The route is deliberately untouched and still reachable ("open results page"),
 * because it is what every existing deep link, bookmark and QA hand-off points
 * at — and it is the surface the QA tiers render.
 *
 * `key={containerId}` is load-bearing. The panel holds a lot of per-container
 * state (active page, viewer position, unsaved highlight draft, template
 * filter); without a remount, opening a second container from the listing would
 * show its results underneath the first one's cursor — and, worse, keep the
 * first one's unsaved highlight draft live against the wrong document.
 */
export function ContainerResultsOverlay({
  containerId,
  containerLabel,
  deepLink,
  renderPageFooter,
  onClose
}: ContainerResultsOverlayProps) {
  const t = useTranslations('ocr');

  return (
    <Dialog open={containerId != null} onOpenChange={() => onClose()}>
      <DialogContent className='flex h-[96dvh] max-h-[96dvh] w-[98vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[110rem]'>
        <DialogHeader className='shrink-0 space-y-0 border-b px-5 py-3'>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <FileText className='h-4 w-4 shrink-0' />
            <span className='min-w-0 truncate'>{containerLabel}</span>
            {containerId != null && (
              // The escape hatch out of the overlay: a shareable URL, and the
              // surface to use when the panel wants the whole viewport. `me-6`
              // clears the dialog's own close button, which is positioned at
              // the inline end.
              <Button
                variant='ghost'
                size='sm'
                className='ms-auto me-6 h-7 shrink-0 text-xs font-normal'
                asChild
              >
                <Link href={`/dashboard/ocr-containers/${containerId}`}>
                  <ExternalLink className='me-1.5 h-3 w-3' />
                  {t('openResultsPage')}
                </Link>
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-4 py-3'>
          {containerId != null && (
            <OcrResultsPage
              key={containerId}
              containerId={containerId}
              surface='ocr'
              deepLink={deepLink}
              renderPageFooter={renderPageFooter}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
